import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { PublicAgent } from '@magic/schemas/agents';
import { Reset, Theme } from '@resultadosdigitais/tangram-components';
import Color from 'color';

import { WithChildren } from '@exact-magic/react-utils';
import { useAgent } from '../context/agent';

const buildTheme = (agent: PublicAgent) => {
  const base = Color(agent.color || '#fd2199');
  const [r, g, b] = base.rgb().array();
  const contrast =
    r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#ffffff';
  return {
    '--primary-foreground': contrast,
    '--color-primary-10': base.lighten(0.9).hex(),
    '--color-primary-20': base.lighten(0.5).hex(),
    '--color-primary-30': base.lighten(0.3).hex(),
    '--color-primary-40': base.lighten(0.2).hex(),
    '--color-primary-50': base.lighten(0.1).hex(),
    '--color-primary-60': base.hex(),
    '--color-primary-70': base.darken(0.1).hex(),
    '--color-primary-80': base.darken(0.2).hex(),
    '--color-primary-90': base.darken(0.3).hex(),
    '--color-primary-100': base.darken(0.4).hex(),
    '--primary-border': 'var(--color-primary-60)',
    '--primary-border-hover': 'var(--color-primary-80)',
    '--primary-icon': 'var(--color-primary-70)',
    '--primary-icon-hover': 'var(--color-primary-80)',
    '--primary-surface-high-emphasis': 'var(--color-primary-60)',
    '--primary-surface-low-emphasis': 'var(--color-primary-20)',
    '--primary-surface-hover': 'var(--color-primary-50)',
    '--primary-text': 'var(--color-primary-70)',
    '--primary-text-hover': 'var(--color-primary-80)',
    '--primary-text-on-color': 'var(--color-primary-60)',
    '--primary-text-on-color-hover': 'var(--color-primary-70)',
  } as CSSProperties;
};

export const useTheme = (agent: PublicAgent): CSSProperties => {
  const [theme, setTheme] = useState<CSSProperties>({});

  useEffect(() => {
    const theme = buildTheme(agent);
    setTheme(theme);
  }, [agent]);

  return theme;
};

export const usePageSettings = (agent: PublicAgent): void => {
  const faviconNode = useRef(document.querySelector('link[rel="icon"]'));

  const replaceFavicon = (img?: string) => {
    if (!img) return;

    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = img;

    if (faviconNode.current) document.head.removeChild(faviconNode.current);

    document.head.appendChild(link);
    faviconNode.current = link;
  };

  const replaceTitle = (
    name: string = 'Assistente',
    description: string | null
  ) => {
    document.title = `${name} | ${description || 'Powered by MentorAI'}`;
  };

  useEffect(() => {
    replaceFavicon(agent.avatar?.url);
    replaceTitle(agent.name, agent.description);
  }, [agent]);
};

export const PageSettings = ({ children }: WithChildren) => {
  const agent = useAgent();
  usePageSettings(agent);
  const theme = useTheme(agent);
  return (
    <Theme style={theme}>
      <Reset />
      {children}
    </Theme>
  );
};
